// /branschstudier/prisindex-2026 — linkable asset (authority).
// Öppet prisindex för svensk filmproduktion: format, roller, städer.
// Schema: Report + Dataset + Claim per siffra + speakable + breadcrumbs.
// Siffrorna bor i src/lib/data/prisindex-2026.ts och delas med CSV/JSON.

import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorByline } from "@/components/site/AuthorByline";
import { EDITORIAL_PEOPLE, authorId, authorPath } from "@/content/editorial";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, claim, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, BRAND, ORG_ID, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import {
  CITY_INDEX,
  FORMAT_INDEX,
  KEY_STATS,
  PRISINDEX_LICENSE,
  PRISINDEX_LICENSE_URL,
  PRISINDEX_METHOD,
  PRISINDEX_PATH,
  PRISINDEX_PUBLISHED,
  PRISINDEX_TITLE,
  ROLE_INDEX,
  range,
} from "@/lib/data/prisindex-2026";

const PATH = PRISINDEX_PATH;
const TITLE = PRISINDEX_TITLE;
const DESCRIPTION =
  "Öppet prisindex för svensk filmproduktion 2026: prisspann per format, dagspriser per roll och prisskillnad mellan Stockholm, Göteborg och Malmö. Fri att citera under CC BY 4.0 — ladda ner som CSV.";
const PUBLISHED = PRISINDEX_PUBLISHED;
const CSV_URL = "/api/public/prisindex-2026.csv";

const EMBED_SNIPPET = `<a href="${abs(PATH)}">Prisindex filmproduktion Sverige 2026</a> — data från Fenny (CC BY 4.0)`;

const IFRAME_SNIPPET = `<iframe src="${abs("/embed/prisindex-2026")}" title="Prisindex filmproduktion Sverige 2026 — Fenny" width="100%" height="620" loading="lazy" style="border:1px solid #e5e7eb;border-radius:12px"></iframe>`;

/** Filtrera indexet per format via ?format=… — ger delbara, citerbara URL:er. */
const slugifyFormat = (s: string) =>
  s
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const Route = createFileRoute("/branschstudier_/prisindex-2026")({
  validateSearch: (search: Record<string, unknown>): { format?: string } =>
    typeof search.format === "string" ? { format: search.format } : {},
  head: () => {
    const ogImage = SOCIAL_IMAGE_URL;

    const report = {
      "@context": "https://schema.org",
      "@type": "Report",
      name: TITLE,
      headline: TITLE,
      description: DESCRIPTION,
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      inLanguage: "sv-SE",
      url: abs(PATH),
      mainEntityOfPage: abs(PATH),
      author: {
        "@type": "Person",
        "@id": authorId("mattias"),
        name: EDITORIAL_PEOPLE.mattias.name,
        jobTitle: EDITORIAL_PEOPLE.mattias.role,
        url: `https://fenny.se${authorPath("mattias")}`,
        sameAs: EDITORIAL_PEOPLE.mattias.linkedin ? [EDITORIAL_PEOPLE.mattias.linkedin] : [],
        worksFor: { "@id": ORG_ID },
      },
      publisher: { "@id": ORG_ID, "@type": "Organization", name: BRAND },
      license: PRISINDEX_LICENSE_URL,
      isPartOf: {
        "@type": "CollectionPage",
        name: "Branschstudier",
        url: abs("/branschstudier"),
      },
    };

    const dataset = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: TITLE,
      description:
        "Prisspann per filmformat, dagspriser per produktionsroll och regionalt prisindex för svensk filmproduktion 2026. Aggregerat från Fennys leveransdata och offentliga offertunderlag.",
      url: abs(PATH),
      keywords: [
        "filmproduktion",
        "pris",
        "prisindex",
        "Sverige",
        "2026",
        "företagsfilm",
        "reklamfilm",
      ],
      temporalCoverage: "2024/2026",
      spatialCoverage: "Sverige",
      creator: { "@id": ORG_ID },
      author: { "@id": authorId("mattias") },
      license: PRISINDEX_LICENSE_URL,
      measurementTechnique: `Aggregering av ${PRISINDEX_METHOD.sampleProductions} produktioner och ${PRISINDEX_METHOD.sampleQuotes} offertunderlag, ${PRISINDEX_METHOD.percentile}.`,
      variableMeasured: [
        "Produktionspris per format (SEK)",
        "Dagspris per produktionsroll (SEK)",
        "Regionalt prisindex (Göteborg = 100)",
      ],
      distribution: [
        {
          "@type": "DataDownload",
          name: "Prisindex 2026 (CSV)",
          encodingFormat: "text/csv",
          contentUrl: abs(CSV_URL),
        },
        {
          "@type": "DataDownload",
          name: "Branschstatistik (JSON)",
          encodingFormat: "application/json",
          contentUrl: abs("/api/public/branschstats"),
        },
      ],
    };

    const claimLds = KEY_STATS.map((s) =>
      claim({ text: s.text, path: PATH, datePublished: PUBLISHED, about: s.about }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Branschstudier", path: "/branschstudier" },
      { name: "Prisindex 2026", path: PATH },
    ]);

    const speakableLd = { ...speakable([".lede", ".key-stat"]), url: abs(PATH) };

    return {
      meta: [
        { title: `${TITLE} — öppen data | Fenny` },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: ogImage },
      ],
      links: [
        { rel: "canonical", href: abs(PATH) },
        { rel: "license", href: PRISINDEX_LICENSE_URL },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/branschstudier/prisindex-2026"})) },
        { type: "application/ld+json", children: JSON.stringify(report) },
        { type: "application/ld+json", children: JSON.stringify(dataset) },
        ...claimLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
      ],
    };
  },
  component: PrisindexPage,
});

function PrisindexPage() {
  const { format } = Route.useSearch();
  const activeFormat = FORMAT_INDEX.find((f) => slugifyFormat(f.format) === format);
  const visibleFormats = activeFormat ? [activeFormat] : FORMAT_INDEX;

  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Branschstudie · Publicerad {PUBLISHED} · {PRISINDEX_LICENSE}
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">{TITLE}</h1>
          <p className="lede text-lg text-muted-foreground">{DESCRIPTION}</p>
          <AuthorByline authorKey="mattias" date={PUBLISHED} reviewedDate={PUBLISHED} />
          <div className="flex flex-wrap gap-3">
            <a
              href={CSV_URL}
              className="inline-flex items-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
              download
            >
              Ladda ner hela indexet (CSV)
            </a>
            <Button to="/priser" variant="outline">
              Se Fennys egna priser
            </Button>
            <Button to="/for-journalister" variant="outline">
              För journalister
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Nyckeltal — citerbara</h2>
          <ul className="space-y-4">
            {KEY_STATS.map((s) => (
              <li key={s.text} className="key-stat rounded-lg border border-border bg-card p-5">
                <p className="text-base">”{s.text}”</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ämne: {s.about} · Källa: Fenny prisindex, {PUBLISHED}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Prisindex per format</h2>
          <div className="flex flex-wrap gap-2">
            <Link
              to={PATH}
              search={{}}
              className={`rounded-full border px-3 py-1 text-xs ${
                activeFormat
                  ? "border-border text-muted-foreground"
                  : "border-primary bg-primary text-primary-foreground"
              }`}
            >
              Alla format
            </Link>
            {FORMAT_INDEX.map((f) => {
              const slug = slugifyFormat(f.format);
              const isActive = slug === format;
              return (
                <Link
                  key={f.format}
                  to={PATH}
                  search={{ format: slug }}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    isActive
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {f.format}
                </Link>
              );
            })}
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Format</th>
                  <th className="px-4 py-3">Längd</th>
                  <th className="px-4 py-3">Prisspann 2026</th>
                  <th className="px-4 py-3">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {visibleFormats.map((f) => (
                  <tr key={f.format} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{f.format}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.length}</td>
                    <td className="px-4 py-3 text-muted-foreground">{range(f.min, f.max)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Spann avser produktionskostnad exklusive mediaköp, {PRISINDEX_METHOD.percentile}.
            {activeFormat
              ? ` Filtrerat på ${activeFormat.format} — länken är delbar.`
              : " Klicka på ett format för en delbar URL."}
          </p>
        </div>
      </Section>


      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Dagspriser per roll</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Roll</th>
                  <th className="px-4 py-3">Dagspris 2026</th>
                </tr>
              </thead>
              <tbody>
                {ROLE_INDEX.map((r) => (
                  <tr key={r.role} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{r.role}</td>
                    <td className="px-4 py-3 text-muted-foreground">{range(r.min, r.max)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">
            Regionalt prisindex ({PRISINDEX_METHOD.referenceCity})
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Ort</th>
                  <th className="px-4 py-3">Index</th>
                  <th className="px-4 py-3">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {CITY_INDEX.map((c) => (
                  <tr key={c.city} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{c.city}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.index}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 sm:grid-cols-5">
            {CITY_INDEX.map((c) => (
              <div key={c.city} className="rounded-lg border border-border bg-card p-4">
                <div className="text-sm text-muted-foreground">{c.city}</div>
                <div
                  className="mt-2 h-2 rounded-full bg-primary"
                  style={{ width: `${Math.round((c.index / 112) * 100)}%` }}
                  aria-hidden="true"
                />
                <div className="mt-2 text-lg font-semibold">{c.index}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Metod</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Urval:</strong>{" "}
              {PRISINDEX_METHOD.sampleProductions} levererade produktioner och{" "}
              {PRISINDEX_METHOD.sampleQuotes} offertunderlag från svenska produktionsbolag.
            </li>
            <li>
              <strong className="text-foreground">Period:</strong> {PRISINDEX_METHOD.period}.
            </li>
            <li>
              <strong className="text-foreground">Spann:</strong> {PRISINDEX_METHOD.percentile}.
            </li>
            <li>
              <strong className="text-foreground">Felmarginal:</strong>{" "}
              {PRISINDEX_METHOD.marginOfError}.
            </li>
            <li>
              <strong className="text-foreground">Exkluderar:</strong>{" "}
              {PRISINDEX_METHOD.excludes}.
            </li>
            <li>
              <strong className="text-foreground">Ortsindex:</strong> jämför identisk omfattning
              (en inspelningsdag, tvåmannateam, klipp och grade) mellan orter.
            </li>
          </ul>
          <p className="text-muted-foreground">
            Sifferunderlaget uppdateras två gånger per år. Hittar du ett fel? Mejla oss via{" "}
            <Link to="/kontakt" className="underline">
              kontaktsidan
            </Link>{" "}
            så rättar vi och noterar ändringen.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Använd datan — så här citerar du</h2>
          <p className="text-muted-foreground">
            Indexet är öppet under {PRISINDEX_LICENSE}. Du får publicera, bearbeta och
            visualisera siffrorna, även kommersiellt, så länge du anger Fenny som källa med
            en klickbar länk.
          </p>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm font-medium">Rekommenderad källhänvisning</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Fenny (2026). <em>{TITLE}</em>. Hämtad från{" "}
              <Link to={PATH} className="underline">
                {abs(PATH)}
              </Link>
              .
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm font-medium">Bädda in hela tabellen (iframe)</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Widgeten uppdateras automatiskt när indexet revideras och innehåller
              källhänvisning till Fenny.
            </p>
            <pre className="mt-2 overflow-x-auto rounded bg-muted p-3 text-xs">
              <code>{IFRAME_SNIPPET}</code>
            </pre>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm font-medium">Kopiera in i din artikel (HTML)</p>
            <pre className="mt-2 overflow-x-auto rounded bg-muted p-3 text-xs">
              <code>{EMBED_SNIPPET}</code>
            </pre>
          </div>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>
              CSV:{" "}
              <a href={CSV_URL} className="underline" download>
                {abs(CSV_URL)}
              </a>
            </li>
            <li>
              JSON:{" "}
              <a href="/api/public/branschstats" className="underline">
                {abs("/api/public/branschstats")}
              </a>
            </li>
            <li>
              Licens:{" "}
              <a
                href={PRISINDEX_LICENSE_URL}
                className="underline"
                rel="license noopener"
                target="_blank"
              >
                {PRISINDEX_LICENSE}
              </a>
            </li>
          </ul>
          <p className="text-muted-foreground">
            Vill ni jämföra mot faktiska paketpriser finns{" "}
            <Link to="/priser" className="underline">
              Fennys prislista
            </Link>
            ,{" "}
            <Link to="/jamfor/filmbolag-vs-filmbyra" className="underline">
              jämförelsen filmbolag vs filmbyrå
            </Link>{" "}
            och{" "}
            <Link to="/branschstudier" className="underline">
              alla branschstudier
            </Link>
            .
          </p>
        </div>
      </Section>
    </main>
  );
}
